<template>
  <div>
    <div class="missionStatement px-3 mt-2 mb-4">
      <div class="content mb-2">
        {{ mission.content }}
        <span class="total">
          {{ $t('extension.userAnalyses.events.created') }} {{ getFromNow(mission.timestamp) }}
        </span>
      </div>
      <div class="d-flex align-center tagged">
        <div class="total mr-2">
          {{ `${numberOfTaggedWhoAreDone} / ${totalNumberOfTagged}` }} {{ $t('extension.userAnalyses.accomplishedMissions') }}
        </div>
        <div
          v-if="!showMore"
          class="d-flex delegates"
        >
          <Chip
            v-for="(item, index) in taggedFiltered"
            :key="index"
            :initials="getInitials(item.colleague)"
            :name="getName(item.colleague)"
            :color="getColor(item.colleague.id)"
            @click="() => scrollTo(item.fulfillmentId)"
            :disabled="!isFulfilled(item.colleague.id)"
            :item="item"
          />
          <button class="more" v-show="showMoreButton" @click="showMorePeople">
            ...
          </button>
        </div>
        <div
          class="d-flex delegates delegates-show-more"
          v-else
        >
          <Chip
            v-for="(item, index) in tagged"
            :key="index"
            :initials="getInitials(item.colleague)"
            :name="getName(item.colleague)"
            :color="getColor(item.colleague.id)"
            @click="() => scrollTo(item.id)"
            :disabled="!isFulfilled(item.colleague.id)"
            :status="item.status"
          />
        </div>
      </div>
    </div>
    <Thread
      v-for="fulfillment in missionFulfillments"
      :thread="fulfillment"
      :replies="getFulfillmentReplies(fulfillment.id)"
      :key="fulfillment.id"
      @save="saveThreadMessage"
      class="thread mb-2"
      :one-liner="true"
    >
      <template v-slot:before-header>
        <FulfillmentVotes
          :analysis-id="analysisId"
          :user-analysis-id="userAnalysisId"
          :fulfillment-id="fulfillment.id"
        />
      </template>
    </Thread>
    <v-btn
      @click="clickFulfill"
      v-if="currentUserFulfillment === undefined && isTagged(user.userId)"
      color="primary"
      small
      elevation="0"
      class="mission-btn"
    >
      {{ $t('mission.fulfill') }}
      <v-icon class="ml-2" small>
        add
      </v-icon>
    </v-btn>
    <v-expand-transition>
      <TextInput
        v-if="fulfilling"
        class="mt-3"
        ref="textInput"
        @cancel="fulfilling = false"
        @save="fullfillMission"
      />
    </v-expand-transition>
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex';
import moment from 'moment';
import { types as missionTypes } from '../../../store/missions';
import { types as userTypes } from '../../../store/user';
import { getColorForId } from '../../../tools/UsersColors';
import FulfillmentVotes from './FulfillmentVotes';
import Thread from './thread/Thread';
import Chip from './Chip';
import TextInput from './TextInput';

export default {
  name: 'Mission',
  components: {
    TextInput,
    Chip,
    Thread,
    FulfillmentVotes,
  },
  props: {
    userAnalysisId: {
      type: String,
      required: true,
    },
    analysisId: {
      type: String,
      required: true,
    },
    mission: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapGetters({
      user: userTypes.getters.userDetails,
      getMissionFulfillments: missionTypes.getters.missionFulfillments,
      getMissionFulfillmentComments: missionTypes.getters.missionFulfillmentComments,
    }),
    missionFulfillments() {
      return this.getMissionFulfillments(this.mission.id);
    },
    currentUserFulfillment() {
      if (!Array.isArray(this.missionFulfillments)) {
        return undefined;
      }

      return this.missionFulfillments.find(ffm => ffm.userId === this.user.userId);
    },
    numberOfTaggedWhoAreDone() {
      if (!Array.isArray(this.missionFulfillments)) {
        return 0;
      }
      return this.missionFulfillments.length;
    },
    totalNumberOfTagged() {
      return this.mission.tagged.length;
    },
    tagged() {
      const tagged = [...this.mission.tagged];
      return tagged.sort((a, b) => {
        const aFulfilled = this.isFulfilled(a.colleague.id);
        const bFulfilled = this.isFulfilled(b.colleague.id);
        if (aFulfilled && !bFulfilled) {
          return -1;
        }
        if (!aFulfilled && bFulfilled) {
          return 1;
        }
        return this.getName(a).localeCompare(this.getName(b));
      });
    },
    taggedFiltered() {
      return this.tagged.slice(0, 6);
    },
    chips() {
      return this.showMore ? this.tagged : this.taggedFiltered;
    },
    showMoreButton() {
      return this.tagged.length > 6;
    },
  },
  methods: {
    ...mapActions({
      writeFulfillment: missionTypes.actions.writeFulfillment,
      writeFulfillmentComment: missionTypes.actions.writeFulfillmentComment,
      fetchFulfillments: missionTypes.actions.fetchFulfillments,
      fetchFulfillmentComments: missionTypes.actions.fetchFulfillmentComments,
    }),
    getFromNow(date) {
      return moment(date).fromNow();
    },
    clickFulfill() {
      this.fulfilling = !this.fulfilling;
      if (this.fulfilling) {
        this.$nextTick(() => this.$refs.textInput.$refs.textArea.focus());
      }
    },
    fullfillMission({ text }) {
      const fulfillment = {
        id: this.userMissionTag(this.user.userId).fulfillmentId,
        status: 'done',
        content: text,
        modifiedOn: Date.now(),
        missionId: this.mission.id,
        userId: this.user.userId,
      };

      this.fulfilling = false;

      this.writeFulfillment({
        analysisId: this.analysisId,
        userAnalysisId: this.userAnalysisId,
        fulfillment,
      });
    },
    getColor(id) {
      return getColorForId(id);
    },
    saveThreadMessage({ content, threadId }) {
      this.writeFulfillmentComment({
        analysisId: this.analysisId,
        userAnalysisId: this.userAnalysisId,
        missionId: this.mission.id,
        fulfillmentId: threadId,
        comment: {
          modifiedOn: Date.now(),
          userId: this.user.userId,
          content,
        },
      });
    },
    getName(colleague) {
      return `${colleague.firstName} ${colleague.lastName}`;
    },
    getInitials(colleague) {
      const names = [colleague.firstName, colleague.lastName];
      const initials = names.map((name) => name.charAt(0));
      return initials.join('');
    },
    scrollTo(id) {
      const element = document.querySelector(`#thread-${id}`);
      element.scrollIntoView(true, { behavior: 'smooth' });
    },
    showMorePeople() {
      this.showMore = true;
    },
    isTagged(userId) {
      return this.tagged.find(t => t.colleague.id === userId);
    },
    isFulfilled(userId) {
      if (!Array.isArray(this.missionFulfillments)) {
        return false;
      }
      return this.missionFulfillments.find(fulfillment => fulfillment.userId === userId);
    },
    userMissionTag(userId) {
      return this.mission.tagged.find(tag => tag.colleague.id === userId);
    },
    getFulfillmentReplies(fulfillmentId) {
      return this.getMissionFulfillmentComments(fulfillmentId);
    },
  },
  data() {
    return {
      fulfilling: false,
      showMore: false,
    };
  },
  created() {
    this.fetchFulfillments({
      analysisId: this.analysisId,
      userAnalysisId: this.userAnalysisId,
      missionId: this.mission.id,
    });

    this.mission.tagged.forEach(tag => {
      this.fetchFulfillmentComments({
        analysisId: this.analysisId,
        userAnalysisId: this.userAnalysisId,
        fulfillmentId: tag.fulfillmentId,
      });
    });
  },
};
</script>
<style scoped>
.content {
  color: #3C44C8;
  color: black;
  font-size: 13px;
}

.missionStatement {
  --mission-statement-background: #f7f7f7;
  --mission-statement-background: #fff;
  border-radius: 0px;
  background-color: var(--mission-statement-background);
  border-left: solid 2px  #3c44c8;
}

.total {
  font-size: 13px;
  color: #8F979F;
}

.delegates {
  position: relative;
}

.delegates > div,
.delegates-show-more > div {
  transition: margin-right 0.2s ease-in-out;
}

.delegates::after {
  content: '';
  position: absolute;
  height: 28px;
  width: 80px;
  left: 70px;
  border-radius: 100px;
  background: linear-gradient(
    to right,
    rgba(0,0,0,0),
    var(--mission-statement-background)
  );
  pointer-events: none;
  transition: all 0.2s ease-in-out;
}

.delegates:hover::after {
  opacity: 0;
  width: 160px;
}

.delegates-show-more > div,
.delegates:hover > div {
  margin-right: 8px;
}

.delegates:hover .more {
  display: block;
  opacity: 1;
}

.delegates-show-more::after {
  opacity: 0;
}

.more {
  margin-left: 10px;
  cursor: pointer;
  user-select: none;
}

.more:hover {
  text-decoration: underline;
  color: blue;
}

.thread {
  scroll-margin-top: 55px;
  padding-left: 16px;
}

.mission-btn {
  text-transform: none;
}
</style>
